import {
  Container,
  Autocomplete,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Alert,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import React, { useState, useEffect, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import useApiEp from "../../../hooks/useApiEp";
import { API_ENDPOINT } from "../../../utils/constants";
import { getErrorResponse, getHeader, toastError } from "../../../utils/util";
import AddToTargetBtn from "./AddToTargetBtn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import { useDialog } from "../../../context/DialogContext";
import { InfoOutlined } from "@mui/icons-material";
import SplitsDisplay from "./SplitsDisplay";
import { UserContext } from "../../../context/UserProvider";
import DatabaseAIAssistant from "./DatabaseAIAssistant";
export const SectionCard = ({ title, children, helpText }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      mb: 3,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
    }}
  >
    <Box display="flex" alignItems="center" mb={2}>
      <Typography variant="h6" fontWeight="600" color="primary">
        {title}
      </Typography>
      {helpText && (
        <Tooltip title={helpText} arrow>
          <IconButton size="small" sx={{ ml: 1 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    {children}
  </Paper>
);

const RdCampaignContactGroup = ({
  campaignPost,
  setCampaignPost,
  setActiveStep,
  contactGroup,
  groupName,
}) => {
  const { data = [], isLoading: loadingList } = useApiEp(
    "/user/get-my-assigned-dbs"
  );

  const contactList =
    groupName && groupName.trim() !== ""
      ? data.filter((item) => item.name === groupName)
      : data;

  const { showDialog } = useDialog();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [contacts, setContacts] = useState([]);

  // AI filtering state
  const [aiFilterResult, setAiFilterResult] = useState();
  const [splitMessage, setSplitMessage] = useState("");
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitsData, setSplitsData] = useState([]);
  const [delegatingCampaign, setDelegatingCampaign] = useState(false);

  // Mapping and count state
  const [mappingPositions, setMappingPositions] = useState([]);
  const [contactCount, setContactCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState(null);
  const { user: userData, loading } = useContext(UserContext);

  useEffect(() => {
    if (selectedGroup) {
      setCountLoading(true);
      setCountError(null);

      fetch(
        `${API_ENDPOINT}/user/get-contacts-count-subcategory?categoryId=${selectedGroup._id}`,
        {
          method: "GET",
          ...getHeader(),
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to fetch contact count");
          }
          return response.json();
        })
        .then((data) => {
          setContactCount(data.count);
          setCountLoading(false);

          const columns = selectedGroup.columns || [];
          const generatedContacts = Array.from({ length: data.count }, () =>
            columns
              .filter((item) => item !== "Phone")
              .reduce(
                (acc, column) => {
                  acc[column] = "****";
                  return acc;
                },
                { phoneNumber: "****" }
              )
          );

          setContacts(generatedContacts);
        })
        .catch((err) => {
          setCountError(err.message);
          setCountLoading(false);
        });
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (campaignPost?.contactsList) {
      setContacts(campaignPost.contactsList);
    }
    if (campaignPost?.contactGroup) {
      setSelectedGroup(campaignPost.contactGroup);
    }
  }, [campaignPost?.contactsList, campaignPost?.contactGroup]);

  useEffect(() => {
    if (campaignPost.bodyParams.length > 0 && !campaignPost.isStaticParams) {
      if (
        campaignPost?.contactMapping &&
        campaignPost.contactMapping.length > 0
      ) {
        setMappingPositions(campaignPost.contactMapping);
      } else {
        setMappingPositions(
          campaignPost.bodyParams.map((item, index) => ({
            type: "dynamic",
            [`param_${index + 1}`]: "",
          }))
        );
      }
    }
  }, [campaignPost.bodyParams, campaignPost.isStaticParams]);

  useEffect(() => {
    if (
      contacts.length > 0 &&
      (!campaignPost.startIndex || !campaignPost.endIndex)
    ) {
      setCampaignPost((prev) => ({
        ...prev,
        startIndex: 0,
        endIndex: contacts.length,
      }));
    }
  }, [contacts, setCampaignPost]);

  const handleMappingChange = (paramIndex, field, newValue) => {
    setMappingPositions((prev) =>
      prev.map((item, index) => {
        if (index !== paramIndex) return item;
        const updatedItem = { ...item };
        updatedItem[field] = newValue;
        return updatedItem;
      })
    );
  };

  // --- Handlers for the new DatabaseAIAssistant component ---
  const handleAIAssistantSuccess = useCallback(
    (responseData) => {
      setAiFilterResult(responseData);
      setCampaignPost((prev) => ({
        ...prev,
        marketingQuery: responseData.fullFilter,
      }));
      setContactCount(responseData.count);

      const columns = selectedGroup?.columns || [];
      const generatedContacts = Array.from({ length: responseData.count }, () =>
        columns
          .filter((item) => item !== "Phone")
          .reduce(
            (acc, column) => {
              acc[column] = "****";
              return acc;
            },
            { phoneNumber: "****" }
          )
      );
      setContacts(generatedContacts);
    },
    [selectedGroup, setCampaignPost]
  );

  const handleAIAssistantError = useCallback(
    (error) => {
      showDialog(
        error?.response?.data?.title || "Error",
        `${getErrorResponse(error)}`,
        {
          type: "error",
          onConfirm: () => {},
        }
      );
    },
    [showDialog]
  );
  // ------------------------------------------------------------

  const handleSplitSubmit = useCallback(async () => {
    try {
      setSplitLoading(true);
      const payload = {
        prompt: splitMessage,
        count: aiFilterResult.count,
      };
      const response = await axios.post(
        `${API_ENDPOINT}/campaign/split-campaign`,
        payload,
        getHeader()
      );
      setSplitsData(response.data.splits);
    } catch (error) {
      showDialog(
        error?.response?.data?.title || "Error",
        `${getErrorResponse(error)}`,
        {
          type: "error",
          onConfirm: () => {},
        }
      );
    } finally {
      setSplitLoading(false);
    }
  }, [aiFilterResult, splitMessage, showDialog]);

  const generateTargetAudience = () => {
    if (!selectedGroup) {
      toast.error("Please select your marketing group");
      return;
    }

    const checkFields = mappingPositions.filter(
      (item, index) => item[`param_${index + 1}`] === ""
    );
    if (checkFields.length > 0) {
      toast.error("Please complete all parameter mappings");
      return;
    }
    if (campaignPost.endIndex <= campaignPost.startIndex) {
      toast.error("End position must be greater than start position");
      return;
    }

    const newContacts = [];
    const slicedContacts = contacts.slice(
      campaignPost.startIndex,
      campaignPost.endIndex
    );
    slicedContacts.forEach((citem) => {
      const contact = { Numbers: citem.phoneNumber };
      mappingPositions.forEach((item, index) => {
        if (item.type === "dynamic") {
          const field = item[`param_${index + 1}`];
          contact[`param_${index + 1}`] = citem[field];
        } else {
          contact[`param_${index + 1}`] = item[`param_${index + 1}`];
        }
      });
      newContacts.push(contact);
    });

    setCampaignPost((prev) => ({
      ...prev,
      contacts: newContacts,
      contactsList: contacts,
      contactMapping: mappingPositions,
      contactGroup: selectedGroup,
      isMarketing: true,
    }));

    toast.success(
      `Added ${
        campaignPost.contactsCount || newContacts.length
      } contacts to campaign audience`
    );
    setActiveStep(2);
  };

  const handleAiCampaignDelegate = useCallback(() => {
    showDialog(
      "Please Confirm",
      "Please confirm if you would like to delegate this campaign to our Agent? You may like to double check once as this confirmation would schedule your campaign and our Agent takes over.",
      {
        type: "info",
        onConfirm: async () => {
          try {
            const payload = {
              aiFilterResult,
              splitsData,
              splitMessage,
              campaignPost,
            };
            setDelegatingCampaign(true);
            const response = await axios.post(
              `${API_ENDPOINT}/campaign/delegate-campaign-to-agent`,
              payload,
              getHeader()
            );
            const resp = response.data;
            showDialog(resp?.title || "Success", resp.message || "", {
              type: "success",
              onConfirm: () => {
                setTimeout(() => {
                  document.location.reload();
                }, 100);
              },
            });
          } catch (error) {
            toastError(error);
          } finally {
            setDelegatingCampaign(false);
          }
        },
      }
    );
  }, [aiFilterResult, splitsData, splitMessage, campaignPost, showDialog]);

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
        Select contacts assigned to you by Smart Growth AI
      </Typography>

      <SectionCard
        title="Select Contact Group"
        helpText="Choose a contact group to use for this campaign"
      >
        <Autocomplete
          size="small"
          loading={loadingList}
          value={selectedGroup}
          onChange={(event, newValue) => setSelectedGroup(newValue)}
          options={contactList || []}
          getOptionLabel={(option) => option.name || ""}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Contact Group"
              placeholder="Search for contact groups..."
              helperText="Select a group containing your target audience"
            />
          )}
          fullWidth
        />

        {countLoading && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2">
              Loading contact information...
            </Typography>
          </Box>
        )}

        {countError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Error loading contacts: {countError}
          </Alert>
        )}

        {contactCount !== null && !countLoading && !countError && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This group contains <strong>{contactCount} contacts</strong>
          </Alert>
        )}
      </SectionCard>

      {contacts.length > 0 && (
        <>
          <SectionCard
            title="Parameter Mapping"
            helpText="Map template parameters to contact fields or set static values"
          >
            <Box
              sx={{ mb: 3, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom fontWeight="500">
                Available Contact Fields:
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {Object.keys(contacts[0])
                  .filter((item) => item !== "_id" && item !== "phoneNumber")
                  .map((item) => (
                    <Chip key={item} label={item} size="small" sx={{ mb: 1 }} />
                  ))}
              </Stack>
              <Typography variant="caption" color="textSecondary">
                Note: Sensitive data is masked for privacy
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {mappingPositions.map((param, index) => {
                const paramName =
                  campaignPost.bodyParams[index] || `Param ${index + 1}`;
                return (
                  <Grid item xs={12} key={`param-${index}`}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography
                        fontStyle={"italic"}
                        fontWeight="500"
                        minWidth={120}
                      >
                        (<code>{paramName}</code>)
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Mapping Type</InputLabel>
                        <Select
                          value={param.type}
                          label="Mapping Type"
                          onChange={(e) =>
                            handleMappingChange(index, "type", e.target.value)
                          }
                        >
                          <MenuItem value="dynamic">Contact Field</MenuItem>
                          <MenuItem value="static">Static Value</MenuItem>
                        </Select>
                      </FormControl>
                      {param.type === "dynamic" && (
                        <FormControl size="small" sx={{ flexGrow: 1 }}>
                          <InputLabel>Select Field</InputLabel>
                          <Select
                            value={param[`param_${index + 1}`]}
                            label="Select Field"
                            onChange={(e) =>
                              handleMappingChange(
                                index,
                                `param_${index + 1}`,
                                e.target.value
                              )
                            }
                          >
                            {Object.keys(contacts[0])
                              .filter(
                                (item) =>
                                  item !== "_id" && item !== "phoneNumber"
                              )
                              .map((field) => (
                                <MenuItem key={field} value={field}>
                                  {field}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      )}
                      {param.type === "static" && (
                        <TextField
                          size="small"
                          fullWidth
                          label="Enter Value"
                          value={param[`param_${index + 1}`]}
                          onChange={(e) =>
                            handleMappingChange(
                              index,
                              `param_${index + 1}`,
                              e.target.value
                            )
                          }
                        />
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            <Box mt={3}>
              {/* Insert the new Reusable Component here */}
              <DatabaseAIAssistant
                groupId={selectedGroup?._id}
                onSuccess={handleAIAssistantSuccess}
                onError={handleAIAssistantError}
              />

              {aiFilterResult && (
                <>
                  <Paper
                    elevation={1}
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: "success.light",
                      color: "success.contrastText",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CheckCircleIcon fontSize="small" />
                      <Typography variant="body1">
                        Found <strong>{aiFilterResult.count}</strong> contacts
                        for your prompt
                      </Typography>
                    </Box>
                  </Paper>
                  {aiFilterResult.count > 1 && (
                    <Box mt={2}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="eg. split by 25 contacts per campaign and repeat every 1 hour"
                        label="How do you want to split your campaign?"
                        value={splitMessage}
                        onChange={(e) => setSplitMessage(e.target.value)}
                        helperText="Splitting helps WhatsApp manage delivery limits and improve engagement by sending in smaller, optimized batches."
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="WhatsApp limits bulk sends. Splitting ensures better delivery and avoids blocking.">
                                <InfoOutlined color="action" fontSize="small" />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  )}
                  {splitsData && splitsData.length > 0 && (
                    <SplitsDisplay splitsData={splitsData} />
                  )}
                </>
              )}
            </Box>
          </SectionCard>
          <AddToTargetBtn onClick={generateTargetAudience} />
        </>
      )}

      {!selectedGroup && !countLoading && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="textSecondary">
            {contactList?.length > 0 ? (
              "Select a contact group to configure your campaign audience"
            ) : (
              <>
                No contact lists available for selection.
                <br />
                Please{" "}
                <a href="/home/help-feedback" target="_blank" rel="noreferrer">
                  contact
                </a>{" "}
                your administrator to avail your contacts lists.
              </>
            )}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default RdCampaignContactGroup;
